import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { Music, Users, Terminal, Shield, Wifi, WifiOff, Server, Clock, Activity, LogOut, AlertTriangle, Mail, Megaphone, X } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import NowPlayingWidget from '@/components/NowPlayingWidget';
import { useGuild } from '@/contexts/GuildContext';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

function StatCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="stat-card"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">{label}</p>
          <p className="text-4xl font-mono-stats font-bold text-white">{value ?? '—'}</p>
          {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
      </div>
    </motion.div>
  );
}

function BotStatusCard({ status, showServers }) {
  const formatUptime = (secs) => {
    if (!secs) return 'N/A';
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="stat-card col-span-full lg:col-span-1"
      data-testid="bot-status-card"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-3 h-3 rounded-full ${status?.online ? 'bg-emerald-400 pulse-online' : 'bg-red-400'}`} />
        <span className={`text-sm font-semibold font-[Outfit] ${status?.online ? 'text-emerald-400' : 'text-red-400'}`}>
          {status?.online ? 'Online' : 'Offline'}
        </span>
      </div>
      <p className="text-xs text-zinc-500 uppercase tracking-widest font-[Manrope] mb-1">Bot Status</p>
      <p className="text-2xl font-mono-stats font-bold text-white truncate">{status?.bot_tag || 'Not Connected'}</p>
      <div className="mt-3 space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-zinc-500">Latency</span>
          <span className="text-zinc-300 font-mono-stats">{status?.latency_ms ? `${status.latency_ms}ms` : '—'}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-zinc-500">Uptime</span>
          <span className="text-zinc-300 font-mono-stats">{formatUptime(status?.uptime_seconds)}</span>
        </div>
        {showServers && (
          <div className="flex justify-between text-xs">
            <span className="text-zinc-500">Servers</span>
            <span className="text-zinc-300 font-mono-stats">{status?.guild_count ?? 0}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

function BannedServersBanner() {
  const [bans, setBans] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/my-bans`, authHeader())
      .then(r => setBans(r.data.bans || []))
      .catch(() => setBans([]));
  }, []);

  if (!bans || bans.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4"
      data-testid="banned-servers-banner"
    >
      <p className="text-sm font-semibold text-white font-[Outfit] mb-2">You're banned from {bans.length} server{bans.length > 1 ? 's' : ''} the bot is in</p>
      <div className="space-y-2">
        {bans.map(b => (
          <div key={b.guild_id} className="flex items-center gap-3 bg-[var(--bg-hover)] rounded-lg px-3 py-2" data-testid={`banned-${b.guild_id}`}>
            {b.guild_icon ? (
              <img src={b.guild_icon} alt="" className="w-7 h-7 rounded-lg flex-shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-lg bg-red-500/20 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium font-[Manrope] truncate">{b.guild_name}</p>
              {b.reason && <p className="text-xs text-zinc-500 font-[Manrope] truncate">Reason: {b.reason}</p>}
            </div>
            {b.appeal_status ? (
              <span className={`text-[11px] px-2 py-1 rounded-full font-medium capitalize flex-shrink-0 ${
                b.appeal_status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' :
                b.appeal_status === 'rejected' ? 'bg-zinc-700/50 text-zinc-400' : 'bg-amber-500/15 text-amber-400'
              }`}>
                Appeal {b.appeal_status}
              </span>
            ) : (
              <Link
                to={`/appeal/${b.guild_id}`}
                data-testid={`appeal-link-${b.guild_id}`}
                className="text-xs px-3 py-1.5 rounded-lg bg-[#5865F2] text-white font-medium hover:bg-[#4752C4] transition-colors flex-shrink-0 font-[Manrope]"
              >
                Submit Appeal
              </Link>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function OnboardingChecklist({ guildId }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`onboarding_dismissed_${guildId}`) || 'false'); } catch { return false; }
  });

  useEffect(() => {
    if (!guildId || dismissed) return;
    axios.get(`${API}/api/onboarding/${guildId}`, authHeader())
      .then(r => setData(r.data))
      .catch(() => {});
  }, [guildId, dismissed]);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(`onboarding_dismissed_${guildId}`, 'true'); } catch {}
  };

  if (dismissed || !data || data.done === data.total) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="mb-6 bg-[#5865F2]/10 border border-[#5865F2]/30 rounded-xl p-4"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-sm font-semibold text-white font-[Outfit]">Setup checklist ({data.done}/{data.total})</p>
          <p className="text-xs text-zinc-400 font-[Manrope] mt-0.5">Complete these steps to get the most out of the bot.</p>
        </div>
        <button onClick={dismiss} className="text-zinc-500 hover:text-white flex-shrink-0" data-testid="dismiss-onboarding"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {data.checklist.map(item => (
          <Link
            key={item.id}
            to={item.path}
            data-testid={`onboarding-${item.id}`}
            className={`flex items-start gap-2 px-3 py-2 rounded-lg transition-colors ${item.done ? 'opacity-50 pointer-events-none' : 'hover:bg-[var(--bg-hover)]'}`}
          >
            <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 ${item.done ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-600'}`}>
              {item.done && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3L3.5 5.5L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>}
            </div>
            <div>
              <p className={`text-xs font-medium font-[Manrope] ${item.done ? 'text-zinc-500 line-through' : 'text-white'}`}>{item.label}</p>
              {!item.done && <p className="text-[11px] text-zinc-500 font-[Manrope]">{item.description}</p>}
            </div>
          </Link>
        ))}
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { selectedGuildId, refetch, selectedGuild } = useGuild();
  const [overview, setOverview] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leavingId, setLeavingId] = useState(null);

  const [contactModal, setContactModal] = useState(null); // { guildId, name, mode: 'message' | 'broadcast' }
  const [contactText, setContactText] = useState('');
  const [sending, setSending] = useState(false);

  const sendContact = async () => {
    if (!contactText.trim() || !contactModal) return;
    setSending(true);
    try {
      const endpoint = contactModal.mode === 'broadcast' ? 'broadcast' : 'message-owner';
      await axios.post(`${API}/api/admin/${endpoint}`, { message: contactText.trim() }, { params: { guild_id: contactModal.guildId } });
      toast.success(contactModal.mode === 'broadcast' ? `Posted to ${contactModal.name}` : `Message sent to ${contactModal.name}'s owner`);
      setContactModal(null);
      setContactText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const leaveServer = async (guildId, name) => {
    if (!window.confirm(`Remove BeatMetrics from "${name}"? This cannot be undone from the dashboard.`)) return;
    setLeavingId(guildId);
    try {
      await axios.post(`${API}/api/admin/leave-guild`, null, { params: { guild_id: guildId } });
      toast.success(`Left ${name}`);
      setOverview(o => ({ ...o, by_server: o.by_server.filter(s => s.guild_id !== guildId) }));
      refetch?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to leave server');
    } finally {
      setLeavingId(null);
    }
  };

  useEffect(() => {
    if (!selectedGuildId) return;
    setLoading(true);
    const fetchData = async () => {
      try {
        const [ovRes, botRes] = await Promise.all([
          axios.get(`${API}/api/dashboard/overview`, { params: { guild_id: selectedGuildId } }),
          axios.get(`${API}/api/bot/status`),
        ]);
        setOverview(ovRes.data);
        setBotStatus(botRes.data);
      } catch (e) {
        console.error('Dashboard fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(() => {
      axios.get(`${API}/api/bot/status`).then(r => setBotStatus(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedGuildId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  const stats = overview?.stats || {};
  const isOwnerView = !!overview?.by_server;

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="dashboard-main">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-[Outfit] text-white">Dashboard</h1>
          <p className="text-zinc-500 text-sm mt-1 font-[Manrope]">Overview of your Discord server and bot activity</p>
        </div>

        <BannedServersBanner />

        {/* Onboarding checklist for staff */}
        {selectedGuild?.is_staff && !selectedGuild?.is_global && (
          <OnboardingChecklist guildId={selectedGuildId} />
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <BotStatusCard status={botStatus} showServers={isOwnerView} />
          <StatCard
            icon={Users}
            label="Total Members"
            value={(overview?.user_count || 0).toLocaleString()}
            sub={isOwnerView ? `${overview?.guild_count || 0} servers` : overview?.guild_name}
            color="bg-[#5865F2]"
            testId="stat-members"
          />
          <StatCard icon={Music} label="Songs Played" value={(stats.total_plays || 0).toLocaleString()} sub="All time" color="bg-violet-600" testId="stat-songs" />
          <StatCard icon={Terminal} label="Commands Used" value={(stats.total_commands || 0).toLocaleString()} sub="All time" color="bg-emerald-600" testId="stat-commands" />
          <StatCard icon={Users} label="Member Joins" value={(stats.total_joins || 0).toLocaleString()} sub="All time" color="bg-sky-600" testId="stat-joins" />
          <StatCard icon={Shield} label="Mod Actions" value={(stats.total_mod_actions || 0).toLocaleString()} sub="All time" color="bg-red-600" testId="stat-mod-actions" />
          {isOwnerView && (
            <>
              <StatCard icon={Activity} label="Active Servers" value={overview?.guild_count || 0} sub="Bot in" color="bg-amber-600" testId="stat-servers" />
              <StatCard icon={Clock} label="Users Tracked" value={(overview?.user_count || 0).toLocaleString()} sub="Across all servers" color="bg-pink-600" testId="stat-users-tracked" />
            </>
          )}
        </div>

        {/* Charts Row */}
        <NowPlayingWidget />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
          {/* Member Growth */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="lg:col-span-3 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="member-growth-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Member Growth</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Joins over the last 30 days</p>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={overview?.member_growth || []}>
                <defs>
                  <linearGradient id="joinsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#5865F2" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#5865F2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} interval={4} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="joins" name="Joins" stroke="#5865F2" fill="url(#joinsGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Activity Chart */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="lg:col-span-2 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="activity-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Weekly Activity</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Commands & plays last 7 days</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={overview?.activity_chart || []}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="day" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="commands" name="Commands" fill="#5865F2" radius={[4, 4, 0, 0]} />
                <Bar dataKey="plays" name="Music Plays" fill="#D946EF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Per-server breakdown (owner "All Servers" view) */}
        {overview?.by_server && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 mb-6"
            data-testid="by-server-breakdown"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Servers</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Activity breakdown by server, most active first</p>
            <div className="space-y-2">
              {overview.by_server.map(s => (
                <div key={s.guild_id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]" data-testid={`server-row-${s.guild_id}`}>
                  {s.icon ? (
                    <img src={s.icon} alt="" className="w-8 h-8 rounded-lg flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-[#5865F2]/20 flex items-center justify-center flex-shrink-0">
                      <Server size={14} className="text-[#5865F2]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-[Manrope] truncate flex items-center gap-1.5">
                      {s.name}
                      {s.inactive && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full px-1.5 py-0.5">
                          <AlertTriangle size={10} /> Inactive
                        </span>
                      )}
                      {s.issues?.length > 0 && (
                        <span
                          title={s.issues.join('\n')}
                          className="inline-flex items-center gap-1 text-[10px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-full px-1.5 py-0.5"
                        >
                          <AlertTriangle size={10} /> {s.issues.length} issue{s.issues.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-zinc-500">{(s.member_count || 0).toLocaleString()} members</p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm text-white font-mono-stats">{s.total_commands}</p>
                    <p className="text-[10px] text-zinc-500">commands</p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm text-white font-mono-stats">{s.total_plays}</p>
                    <p className="text-[10px] text-zinc-500">plays</p>
                  </div>
                  <button
                    onClick={() => { setContactModal({ guildId: s.guild_id, name: s.name, mode: 'message' }); setContactText(''); }}
                    title="Message this server's owner via DM"
                    data-testid={`message-owner-${s.guild_id}`}
                    className="p-2 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-[#5865F2] hover:border-[#5865F2]/30 transition-colors"
                  >
                    <Mail size={14} />
                  </button>
                  <button
                    onClick={() => { setContactModal({ guildId: s.guild_id, name: s.name, mode: 'broadcast' }); setContactText(''); }}
                    title="Post an announcement to this server"
                    data-testid={`broadcast-${s.guild_id}`}
                    className="p-2 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/30 transition-colors"
                  >
                    <Megaphone size={14} />
                  </button>
                  <button
                    onClick={() => leaveServer(s.guild_id, s.name)}
                    disabled={leavingId === s.guild_id}
                    title="Remove bot from this server"
                    data-testid={`leave-server-${s.guild_id}`}
                    className="p-2 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-red-400 hover:border-red-500/30 transition-colors disabled:opacity-40"
                  >
                    <LogOut size={14} />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Contact modal */}
        {contactModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setContactModal(null)}>
            <div
              className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-xl p-5 w-full max-w-md"
              onClick={e => e.stopPropagation()}
              data-testid="contact-modal"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white font-[Outfit]">
                  {contactModal.mode === 'broadcast' ? `Broadcast to ${contactModal.name}` : `Message ${contactModal.name}'s owner`}
                </h3>
                <button onClick={() => setContactModal(null)} className="text-zinc-500 hover:text-white">
                  <X size={16} />
                </button>
              </div>
              <p className="text-xs text-zinc-500 mb-3 font-[Manrope]">
                {contactModal.mode === 'broadcast'
                  ? 'Posted as an embed in this server\'s announcement/system channel.'
                  : 'Sent as a direct message to this server\'s owner on Discord.'}
              </p>
              <textarea
                value={contactText}
                onChange={e => setContactText(e.target.value)}
                rows={4}
                placeholder="Type your message..."
                data-testid="contact-textarea"
                className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all resize-none"
              />
              <button
                onClick={sendContact}
                disabled={sending || !contactText.trim()}
                data-testid="contact-send-btn"
                className="mt-3 w-full py-2.5 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Plays */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="recent-plays"
          >
            <div className="flex items-center gap-2 mb-4">
              <Music size={16} className="text-[#5865F2]" />
              <h3 className="text-sm font-semibold text-white font-[Outfit]">Recent Plays</h3>
            </div>
            {(overview?.recent_plays || []).length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-8">No music played yet</p>
            ) : (
              <div className="space-y-3">
                {(overview?.recent_plays || []).map((play, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#5865F2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Music size={14} className="text-[#5865F2]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate font-[Manrope]">{play.title}</p>
                      <p className="text-xs text-zinc-500">by {play.username}</p>
                    </div>
                    <p className="text-xs text-zinc-600 font-mono-stats flex-shrink-0">
                      {new Date(play.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Recent Commands */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="recent-commands"
          >
            <div className="flex items-center gap-2 mb-4">
              <Terminal size={16} className="text-emerald-400" />
              <h3 className="text-sm font-semibold text-white font-[Outfit]">Recent Commands</h3>
            </div>
            {(overview?.recent_commands || []).length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-8">No commands used yet</p>
            ) : (
              <div className="space-y-3">
                {(overview?.recent_commands || []).map((cmd, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Terminal size={14} className="text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-mono-stats">/{cmd.command}</p>
                      <p className="text-xs text-zinc-500">by {cmd.username}</p>
                    </div>
                    <p className="text-xs text-zinc-600 font-mono-stats flex-shrink-0">
                      {new Date(cmd.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
